import React from 'react';
import { History, CheckCircle2, XCircle, Gift, Star } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ActivityLogEntry } from '@/utils/storage';
import { componentLogger } from '@/utils/logger';

interface ActivityHistoryProps {
  activityLog?: ActivityLogEntry[];
  familyId?: string;
}

// Helper function to ensure value is a string
const ensureString = (value: unknown, fallback: string = ''): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return String(value);
  // If it's an object, array, or anything else, use fallback
  return fallback;
};

// Helper function to ensure value is a number
const ensureNumber = (value: unknown, fallback: number = 0): number => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number' && !isNaN(value)) return value;
  const parsed = Number(value);
  return isNaN(parsed) ? fallback : parsed;
};

export const ActivityHistory: React.FC<ActivityHistoryProps> = ({ activityLog, familyId }) => {
  if (!familyId) {
    return null; // Only show for families
  }

  // Ensure activityLog is an array and validate entries
  const rawActivities = Array.isArray(activityLog) ? activityLog : [];

  // Filter and validate activity log entries
  const activities = rawActivities.filter((entry) => {
    try {
      // Validate required fields exist and are of correct type
      if (!entry || typeof entry !== 'object') return false;
      if (!entry.id || typeof entry.id !== 'string') return false;
      if (!entry.userId || typeof entry.userId !== 'string') return false;
      if (!entry.userName || typeof entry.userName !== 'string') return false;
      if (!entry.action || typeof entry.action !== 'string') return false;
      if (!entry.timestamp || typeof entry.timestamp !== 'string') return false;

      // Validate timestamp is a valid date
      const date = new Date(entry.timestamp);
      if (isNaN(date.getTime())) return false;

      // Validate details is an object (can be empty)
      if (entry.details && typeof entry.details !== 'object') return false;

      return true;
    } catch (error) {
      componentLogger.warn({ error, entryId: entry?.id }, '[ActivityHistory] Invalid entry filtered out');
      return false;
    }
  });

  // Sort by timestamp descending (newest first) with error handling
  const sortedActivities = [...activities].sort((a, b) => {
    try {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA;
    } catch (error) {
      componentLogger.error({ error }, '[ActivityHistory] Sort error');
      return 0;
    }
  });

  // Show last 20 entries
  const recentActivities = sortedActivities.slice(0, 20);

  // Ensure counts are numbers for safe rendering
  const recentCount = ensureNumber(recentActivities?.length, 0);
  const totalCount = ensureNumber(activities?.length, 0);

  const getActionIcon = (action: ActivityLogEntry['action']) => {
    switch (action) {
      case 'task_completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'task_uncompleted':
        return <XCircle className="h-4 w-4 text-gray-400" />;
      case 'reward_claimed':
        return <Gift className="h-4 w-4 text-purple-500" />;
      case 'stars_adjusted':
        return <Star className="h-4 w-4 text-yellow-500" />;
      default:
        return <History className="h-4 w-4 text-gray-400" />;
    }
  };

  const getActionText = (entry: ActivityLogEntry) => {
    try {
      // Ensure details exist and extract values safely using ensureString/ensureNumber
      const details = entry.details || {};
      const taskTitle = ensureString(details.taskTitle, 'a task');
      const rewardTitle = ensureString(details.rewardTitle, 'a reward');
      const starsEarned = ensureNumber(details.starsEarned, 0);
      const starsAdjusted = ensureNumber(details.starsAdjusted, 0);

      switch (entry.action) {
        case 'task_completed':
          return (
            <>
              completed <span className="font-medium">{taskTitle}</span>
              {starsEarned > 0 && (
                <span className="text-yellow-600"> +{String(starsEarned)}⭐</span>
              )}
            </>
          );
        case 'task_uncompleted':
          return (
            <>
              uncompleted <span className="font-medium">{taskTitle}</span>
            </>
          );
        case 'reward_claimed':
          return (
            <>
              claimed <span className="font-medium">{rewardTitle}</span>
            </>
          );
        case 'stars_adjusted':
          return (
            <>
              adjusted stars by{' '}
              <span className="font-medium">
                {starsAdjusted > 0 ? '+' : ''}
                {String(starsAdjusted)}
              </span>
            </>
          );
        default:
          return ensureString(entry.action, 'performed an action');
      }
    } catch (error) {
      componentLogger.error({ error, entryId: entry?.id }, '[ActivityHistory] Error rendering action text');
      return 'performed an action';
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      // Wrap all numbers in String() to prevent object rendering
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${String(diffMins)}m ago`;
      if (diffHours < 24) return `${String(diffHours)}h ago`;
      if (diffDays < 7) return `${String(diffDays)}d ago`;

      // Ensure toLocaleDateString returns a string
      const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      return String(dateStr);
    } catch (error) {
      componentLogger.error({ error, timestamp }, '[ActivityHistory] Error formatting time');
      return 'recently';
    }
  };

  if (recentCount === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Activity History
          </CardTitle>
          <CardDescription>See what everyone has been up to</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No activity yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Activity History
        </CardTitle>
        <CardDescription>
          Recent activity (last {String(recentCount)} of {String(totalCount)})
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {recentActivities.map((entry) => {
            try {
              // Ensure safe rendering of all fields using helper functions
              const userName = ensureString(entry.userName, 'Unknown');
              const timestamp = ensureString(entry.timestamp, '');
              const entryId = ensureString(entry.id, `entry-${Math.random()}`);

              // Validate that we have required values
              if (!userName || !timestamp) {
                componentLogger.warn({ entryId }, '[ActivityHistory] Skipping entry with missing userName or timestamp');
                return null;
              }

              return (
                <div
                  key={entryId}
                  className="flex items-start gap-2 text-sm p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="mt-0.5">{getActionIcon(entry.action)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{userName}</span>{' '}
                      {getActionText(entry)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatTime(timestamp)}
                    </p>
                  </div>
                </div>
              );
            } catch (error) {
              componentLogger.error({ error, entryId: entry?.id }, '[ActivityHistory] Error rendering entry');
              return null;
            }
          })}
        </div>
      </CardContent>
    </Card>
  );
};
